import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Paper, Typography
} from '@mui/material';
import { getRentalAgreementTemplate, uploadRentalAgreementTemplate } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { resolvePhotoUrl } from '../../utils/photoUrl';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const RentalAgreement = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRentalAgreementTemplate();
      setTemplate(res.data || null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load agreement template'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const data = await fileToBase64(file);
      const res = await uploadRentalAgreementTemplate({
        filename: file.name,
        mime: file.type || 'application/pdf',
        data
      });
      setTemplate(res.data);
      setSuccess('Rental agreement uploaded. It will be used for every rental.');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to upload agreement'));
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = template?.url ? resolvePhotoUrl(template.url) : '';
  const isPdf = String(template?.mime || template?.filename || '').toLowerCase().includes('pdf');
  const isImage = String(template?.mime || '').startsWith('image/');

  return (
    <Box>
      <Typography variant="h5" fontWeight={700}>Rental agreement</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload the company rental agreement. Every rental can print this agreement filled with that contract’s customer, items, serials, and dates.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Company template</Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : template ? (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Current file: <strong>{template.filename}</strong>
              {template.uploaded_at ? ` · uploaded ${new Date(template.uploaded_at).toLocaleString()}` : ''}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No agreement uploaded yet. Upload a PDF or image of your rental agreement.
            </Typography>
          )}
          <Button variant="contained" component="label" disabled={saving}>
            {saving ? 'Uploading…' : template ? 'Replace agreement' : 'Upload agreement'}
            <input hidden type="file" accept=".pdf,image/*,.doc,.docx" onChange={onFile} />
          </Button>
          {previewUrl && (
            <Button href={previewUrl} target="_blank" rel="noreferrer" sx={{ ml: 1 }}>
              Open file
            </Button>
          )}
        </CardContent>
      </Card>

      {previewUrl && (isPdf || isImage) && (
        <Paper sx={{ p: 1, overflow: 'hidden' }}>
          {isPdf ? (
            <iframe title="Rental agreement" src={previewUrl} style={{ width: '100%', height: 720, border: 0 }} />
          ) : (
            <Box component="img" src={previewUrl} alt="Rental agreement" sx={{ maxWidth: '100%' }} />
          )}
        </Paper>
      )}
    </Box>
  );
};

export default RentalAgreement;
